import { unwrapDashboardRoot } from 'pages/ReportsPage/components/AnalyticsDashboards/unwrapDashboardRoot';

export const TIMEFRAMES = ['Morning', 'Evening', 'Overnight'] as const;
export const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

const API_TIMEFRAME_LABELS: Record<string, string> = {
  morning: 'Morning',
  evening: 'Evening',
  night: 'Overnight',
  overnight: 'Overnight',
};

export function toTimeframeLabel(apiTimeframe: string): string {
  return API_TIMEFRAME_LABELS[apiTimeframe.trim().toLowerCase()] ?? apiTimeframe;
}

export function toNumber(value: number | string | null | undefined): number {
  if (value == null) return 0;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

const TIMEFRAME_ORDER = new Map<string, number>(TIMEFRAMES.map((label, index) => [label, index]));

export function sortByTimeframe<T extends { timeframe: string }>(items: T[]): T[] {
  return [...items].sort(
    (a, b) =>
      (TIMEFRAME_ORDER.get(a.timeframe) ?? TIMEFRAMES.length) -
      (TIMEFRAME_ORDER.get(b.timeframe) ?? TIMEFRAMES.length),
  );
}

export function toNullableNumber(value: number | string | null | undefined): number | null {
  if (value == null) return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export interface PeakCalloutCell {
  timeframe: string;
  dayOfWeek: string;
  shiftCount: number;
}

export interface PeakCalloutHoursResponseItem {
  shift_timeframe: string;
  days: Record<string, number | string>;
}

export interface ResolutionSpeedCell {
  timeframe: string;
  dayOfWeek: string;
  avgHours: number | null;
}

export interface CalloutResolutionPerTimeframeResponse {
  callout_resolution_per_timeframe: Record<string, Record<string, number | string>>;
}

function normalizeDayKeys(
  days: Record<string, number | string> | null | undefined,
): Record<string, number | string> {
  return Object.fromEntries(
    Object.entries(days ?? {}).map(([day, value]) => [day.trim().toLowerCase(), value]),
  );
}

export function mapPeakCalloutHours(response: unknown): PeakCalloutCell[] {
  // API-5599: `peak_callout_hours` went from a bare array to `{ peak_callout_hours: [...] }`.
  const rows =
    unwrapDashboardRoot<PeakCalloutHoursResponseItem[]>(response, 'peak_callout_hours') ?? [];
  const byTimeframe = new Map(
    rows.map((item) => [toTimeframeLabel(item.shift_timeframe), normalizeDayKeys(item.days)]),
  );
  const cells: PeakCalloutCell[] = [];
  for (const timeframe of TIMEFRAMES) {
    const days = byTimeframe.get(timeframe) ?? {};
    for (const dayOfWeek of DAYS_OF_WEEK) {
      cells.push({ timeframe, dayOfWeek, shiftCount: toNumber(days[dayOfWeek.toLowerCase()]) });
    }
  }
  return cells;
}

export function mapResolutionSpeed(
  response: CalloutResolutionPerTimeframeResponse,
): ResolutionSpeedCell[] {
  const data = response.callout_resolution_per_timeframe ?? {};
  const byTimeframe = new Map(
    Object.entries(data).map(([apiKey, days]) => [
      toTimeframeLabel(apiKey),
      normalizeDayKeys(days),
    ]),
  );
  const cells: ResolutionSpeedCell[] = [];
  for (const timeframe of TIMEFRAMES) {
    const days = byTimeframe.get(timeframe) ?? {};
    for (const dayOfWeek of DAYS_OF_WEEK) {
      cells.push({
        timeframe,
        dayOfWeek,
        avgHours: toNullableNumber(days[dayOfWeek.toLowerCase()]),
      });
    }
  }
  return cells;
}
